import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Table,
  Badge,
  Select,
  Switch,
} from '@radix-ui/themes';
import { ReloadIcon, SpeakerLoudIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import AdminLayout from './AdminLayout';
import {
  adminVoiceApi, AdminVoiceCallRow, AdminVoiceCallsResponse, AdminVoiceQueueResponse,
} from '../../services/api';
import { getApiUrl } from '../../services/api';

/**
 * Cross-clinic review of every voice call (PHA-962), including the off-script
 * moments PHA-965 requires a person to see the same day.
 *
 * Read-only by design. No charts either — the ticket is explicit that the
 * numbers should be correct and reviewable before anyone visualises them, and
 * nobody yet knows which number they would act on.
 */

const OUTCOMES = ['completed', 'opted_out', 'no_answer', 'voicemail', 'greeting_dropoff', 'hung_up', 'abandoned', 'failed', 'unknown'];

const OUTCOME_COLOR: Record<string, 'green' | 'gray' | 'amber' | 'red' | 'orange' | 'blue'> = {
  completed: 'green',
  opted_out: 'orange',
  no_answer: 'gray',
  voicemail: 'blue',
  greeting_dropoff: 'amber',
  hung_up: 'orange',
  abandoned: 'amber',
  failed: 'red',
  unknown: 'gray',
};

const PAGE_SIZE = 50;

const formatDuration = (seconds: number | null): string => {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

// The scheduler rewrites the voice line queue every 30-second poll.
const QUEUE_REFRESH_MS = 30_000;

const formatWait = (sinceIso: string, now: number = Date.now()): string => {
  const minutes = Math.max(0, Math.floor((now - new Date(sinceIso).getTime()) / 60_000));
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const outcomeLabel = (outcome: string | null): string =>
  (outcome || 'unknown').replace(/_/g, ' ');

const AdminVoiceCallsPage: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<AdminVoiceCallRow[]>([]);
  const [summary, setSummary] = useState<AdminVoiceCallsResponse['summary']>({
    total: 0, byOutcome: {}, completionRate: null,
  });
  const [clinics, setClinics] = useState<Array<{ id: string; name: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const [organizationId, setOrganizationId] = useState<string>('all');
  const [outcome, setOutcome] = useState<string>('all');
  const [validationStatus, setValidationStatus] = useState<string>('all');
  const [endedAbruptly, setEndedAbruptly] = useState(false);
  const [offScriptOnly, setOffScriptOnly] = useState(false);
  // PHA-1272: test calls (CI, Voice Lab, test patients) are hidden unless asked for.
  // Test calls (CI, Voice Lab, test patients) are listed by default — the team
  // reviews its own test calls here; hide them for clean outcome numbers.
  const [hideTest, setHideTest] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminVoiceApi.listCalls({
        organizationId: organizationId === 'all' ? undefined : organizationId,
        outcome: outcome === 'all' ? undefined : outcome,
        validationStatus: validationStatus === 'all' ? undefined : validationStatus,
        endedAbruptly: endedAbruptly ? 'true' : undefined,
        offScriptOnly: offScriptOnly ? 'true' : undefined,
        includeTest: hideTest ? undefined : 'true',
        from: from || undefined,
        to: to || undefined,
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });
      setRows(data.rows);
      setSummary(data.summary);
      setHasMore(data.hasMore);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load voice calls');
    } finally {
      setLoading(false);
    }
  }, [organizationId, outcome, validationStatus, endedAbruptly, offScriptOnly, hideTest, from, to, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminVoiceApi.listClinics()
      .then((d) => setClinics(d.clinics))
      .catch(() => setClinics([]));
  }, []);

  // Voice line queue — live, not filtered: it is about now, not the call history below.
  const [voiceQueue, setVoiceQueue] = useState<AdminVoiceQueueResponse | null>(null);
  const loadQueue = useCallback(() => {
    adminVoiceApi.queueStatus()
      .then(setVoiceQueue)
      .catch(() => setVoiceQueue(null));
  }, []);

  useEffect(() => {
    loadQueue();
    const timer = setInterval(loadQueue, QUEUE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadQueue]);

  // Any filter change invalidates the current page number.
  const onFilterChange = (apply: () => void) => { apply(); setPage(1); };

  return (
    <AdminLayout
      title="Voice Calls"
      subtitle="Every voice call across all clinics, newest first"
    >
      <Flex direction="column" gap="4">
        {/* PHA-1281 voice line queue: VAPI allows a fixed number of calls on the line at once.
            Scheduled calls stop short of it; the rest wait here instead of being texted. */}
        {voiceQueue && (
          <Card data-testid="voice-line-queue">
            <Flex gap="5" wrap="wrap" align="center">
              <Box>
                <Text size="1" color="gray">On the line</Text>
                <Text as="div" size="6" weight="bold" data-testid="voice-lines-on-air">
                  {voiceQueue.lines.onAir} / {voiceQueue.lines.totalLimit}
                </Text>
                <Text size="1" color="gray">scheduled calls stop at {voiceQueue.lines.scheduledLimit}</Text>
              </Box>
              <Box>
                <Text size="1" color="gray">Queued</Text>
                <Text
                  as="div"
                  size="6"
                  weight="bold"
                  color={voiceQueue.queue && voiceQueue.queue.queued > 0 ? 'amber' : undefined}
                  data-testid="voice-queue-count"
                >
                  {voiceQueue.queue ? voiceQueue.queue.queued : '—'}
                </Text>
                {voiceQueue.queue?.oldestQueuedSince && (
                  <Text size="1" color="gray" data-testid="voice-queue-oldest">
                    longest wait {formatWait(voiceQueue.queue.oldestQueuedSince)}
                  </Text>
                )}
              </Box>
              {voiceQueue.queue && voiceQueue.queue.byClinic.length > 0 && (
                <Flex gap="2" wrap="wrap" data-testid="voice-queue-by-clinic">
                  {voiceQueue.queue.byClinic.map((c) => (
                    <Badge key={c.organizationId ?? 'none'} color="amber" variant="soft">
                      {c.organizationName || c.organizationId || 'No clinic'}: {c.queued}
                    </Badge>
                  ))}
                </Flex>
              )}
            </Flex>
            {!voiceQueue.queue && (
              <Text size="1" color="gray" as="div" mt="2">The call queue is not set up in this environment yet.</Text>
            )}
          </Card>
        )}

        {/* Summary — the counts the team asks for, over the FILTERED set */}
        <Card>
          <Flex gap="5" wrap="wrap" align="center">
            <Box>
              <Text size="1" color="gray">Total</Text>
              <Text as="div" size="6" weight="bold" data-testid="summary-total">{summary.total}</Text>
            </Box>
            {OUTCOMES.map((o) => (
              <Box key={o}>
                <Text size="1" color="gray" style={{ textTransform: 'capitalize' }}>{outcomeLabel(o)}</Text>
                <Text as="div" size="5" data-testid={`summary-${o}`}>{summary.byOutcome[o] ?? 0}</Text>
              </Box>
            ))}
            <Box>
              <Text size="1" color="gray">Completion rate</Text>
              <Text as="div" size="5" data-testid="summary-completion-rate">
                {summary.completionRate === null ? '—' : `${Math.round(summary.completionRate * 100)}%`}
              </Text>
            </Box>
            {summary.turnLatency && (
              <Box>
                <Text size="1" color="gray">Turn latency p50 / p90</Text>
                <Text as="div" size="5" data-testid="summary-turn-latency">
                  {summary.turnLatency.p50Ms === null
                    ? '—'
                    : `${(summary.turnLatency.p50Ms / 1000).toFixed(1)}s / ${((summary.turnLatency.p90Ms ?? 0) / 1000).toFixed(1)}s`}
                </Text>
                <Text size="1" color={summary.turnLatency.overBudget ? 'amber' : 'gray'} data-testid="summary-latency-over-budget">
                  {summary.turnLatency.overBudget} of {summary.turnLatency.measured} over {(summary.turnLatency.budgetMs / 1000).toFixed(1)}s
                </Text>
              </Box>
            )}
          </Flex>
          {summary.testCallsExcluded && (
            <Text size="1" color="gray" as="div" mt="2">Test calls (CI, Voice Lab, test patients) are not counted.</Text>
          )}
        </Card>

        {/* Filters */}
        <Card>
          <Flex gap="3" wrap="wrap" align="end">
            <Box>
              <Text size="1" color="gray" as="div" mb="1">Clinic</Text>
              {/* position="popper" — an item-aligned Radix Select renders empty here */}
              <Select.Root value={organizationId} onValueChange={(v) => onFilterChange(() => setOrganizationId(v))}>
                <Select.Trigger aria-label="Clinic" />
                <Select.Content position="popper">
                  <Select.Item value="all">All clinics</Select.Item>
                  {clinics.map((c) => (
                    <Select.Item key={c.id} value={c.id}>{c.name || c.id}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            <Box>
              <Text size="1" color="gray" as="div" mb="1">Outcome</Text>
              <Select.Root value={outcome} onValueChange={(v) => onFilterChange(() => setOutcome(v))}>
                <Select.Trigger aria-label="Outcome" />
                <Select.Content position="popper">
                  <Select.Item value="all">All outcomes</Select.Item>
                  {OUTCOMES.map((o) => (
                    <Select.Item key={o} value={o} style={{ textTransform: 'capitalize' }}>{outcomeLabel(o)}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            <Box>
              <Text size="1" color="gray" as="div" mb="1">Validation</Text>
              <Select.Root value={validationStatus} onValueChange={(v) => onFilterChange(() => setValidationStatus(v))}>
                <Select.Trigger aria-label="Validation status" />
                <Select.Content position="popper">
                  <Select.Item value="all">All</Select.Item>
                  <Select.Item value="pending">Pending</Select.Item>
                  <Select.Item value="valid">Valid</Select.Item>
                  <Select.Item value="incomplete">Incomplete</Select.Item>
                  <Select.Item value="needs_review">Needs review</Select.Item>
                  <Select.Item value="invalid">Invalid</Select.Item>
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Plain inputs, matching AdminEligibilityPage — Radix TextField.Root
                in this version does not accept a `type` prop. */}
            <Box>
              <Text size="1" color="gray" as="div" mb="1">From</Text>
              <input
                type="date"
                value={from}
                aria-label="From date"
                onChange={(e) => onFilterChange(() => setFrom(e.target.value))}
                style={{ padding: 6, border: '1px solid var(--gray-7)', borderRadius: 4 }}
              />
            </Box>
            <Box>
              <Text size="1" color="gray" as="div" mb="1">To</Text>
              <input
                type="date"
                value={to}
                aria-label="To date"
                onChange={(e) => onFilterChange(() => setTo(e.target.value))}
                style={{ padding: 6, border: '1px solid var(--gray-7)', borderRadius: 4 }}
              />
            </Box>

            <Flex align="center" gap="2">
              <Switch checked={endedAbruptly} onCheckedChange={(v) => onFilterChange(() => setEndedAbruptly(!!v))} id="ended-abruptly" />
              <Text size="2" as="label" htmlFor="ended-abruptly">Ended abruptly</Text>
            </Flex>

            <Flex align="center" gap="2">
              <Switch checked={offScriptOnly} onCheckedChange={(v) => onFilterChange(() => setOffScriptOnly(!!v))} id="off-script" />
              <Text size="2" as="label" htmlFor="off-script">Off-script only</Text>
            </Flex>

            <Flex align="center" gap="2">
              <Switch checked={hideTest} onCheckedChange={(v) => onFilterChange(() => setHideTest(!!v))} id="hide-test" />
              <Text size="2" as="label" htmlFor="hide-test">Hide test calls</Text>
            </Flex>

            <Button variant="soft" onClick={() => { load(); loadQueue(); }} disabled={loading}>
              <ReloadIcon /> Refresh
            </Button>
          </Flex>
        </Card>

        {error && (
          <Card><Text color="red">{error}</Text></Card>
        )}

        <Card>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Clinic</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>MRN</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Patient</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Date / time</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Duration</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Outcome</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Answers</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Off-script</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Validation</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Recording</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.length === 0 && !loading && (
                <Table.Row>
                  <Table.Cell colSpan={10}>
                    <Text color="gray">No calls match these filters.</Text>
                  </Table.Cell>
                </Table.Row>
              )}
              {rows.map((r) => (
                <Table.Row
                  key={r.id}
                  data-testid={`call-row-${r.id}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/patients/${encodeURIComponent(r.mrn)}?voiceCallLogId=${encodeURIComponent(r.id)}`)}
                >
                  <Table.Cell>{r.clinic || <Text color="gray">Unattributed</Text>}</Table.Cell>
                  <Table.Cell>{r.mrn}</Table.Cell>
                  <Table.Cell>{r.patientName || '—'}</Table.Cell>
                  <Table.Cell>{formatDateTime(r.initiatedAt)}</Table.Cell>
                  <Table.Cell>{formatDuration(r.durationSeconds)}</Table.Cell>
                  <Table.Cell>
                    <Flex gap="1" align="center" wrap="wrap">
                      <Badge color={OUTCOME_COLOR[r.outcome || 'unknown'] || 'gray'} style={{ textTransform: 'capitalize' }}>
                        {outcomeLabel(r.outcome)}
                      </Badge>
                      {/* Visually prominent per the ticket — this is the row a
                          reviewer is looking for. */}
                      {r.endedAbruptly && (
                        <Badge color="red" data-testid={`abrupt-${r.id}`}>
                          <ExclamationTriangleIcon /> Abrupt
                        </Badge>
                      )}
                      {r.offScript.length > 0 && (
                        <Badge color="purple" data-testid={`off-script-${r.id}`}>
                          Off-script ×{r.offScript.length}
                        </Badge>
                      )}
                      {r.outcomeSource === 'inferred' && (
                        <Badge variant="outline" color="gray" title="Reconstructed by backfill, not observed on the call">
                          inferred
                        </Badge>
                      )}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    {r.questionCount > 0 ? `${r.answeredCount}/${r.questionCount}` : '—'}
                  </Table.Cell>
                  {/* What the patient actually said, verbatim (PHA-965). A count
                      alone would mean opening every call to find out whether it
                      needed attention, which is the per-call hunt this screen
                      exists to remove. The assistant's own reply is deliberately
                      not shown: it is the same fixed text every time. */}
                  <Table.Cell style={{ maxWidth: 260 }}>
                    {r.offScript.length === 0 ? (
                      <Text color="gray">—</Text>
                    ) : (
                      <Flex direction="column" gap="1" data-testid={`off-script-statements-${r.id}`}>
                        {r.offScript.map((o, i) => (
                          <Text
                            key={i}
                            size="1"
                            title={o.statement || ''}
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 250,
                            }}
                          >
                            “{o.statement || 'recorded, no text'}”
                          </Text>
                        ))}
                      </Flex>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="soft" color={r.validationStatus === 'needs_review' ? 'amber' : 'gray'}>
                      {(r.validationStatus || '—').replace(/_/g, ' ')}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell onClick={(e) => e.stopPropagation()}>
                    {r.hasRecording ? (
                      // The streaming route presigns; the raw S3 URL never
                      // reaches the browser.
                      <audio
                        controls
                        preload="none"
                        data-testid={`recording-${r.id}`}
                        src={getApiUrl(`/api/voice/voice-calls/${r.id}/recording`)}
                        style={{ height: 32, maxWidth: 220 }}
                      />
                    ) : (
                      <Text color="gray" size="1"><SpeakerLoudIcon /> none</Text>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <Flex justify="between" align="center" mt="3">
            <Text size="1" color="gray">
              {loading ? 'Loading…' : `Page ${page}`}
            </Text>
            <Flex gap="2">
              <Button variant="soft" disabled={page === 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Button variant="soft" disabled={!hasMore || loading} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Flex>
    </AdminLayout>
  );
};

export default AdminVoiceCallsPage;
